import { RoomContext, RoomAudioRenderer } from '@livekit/components-react';
import type { Room } from 'livekit-client';

export interface PersistentVoiceAudioProps {
  /** Live LiveKit room from `useVoiceConversation`; `null` when no call is active. */
  room: Room | null;
  /** LiveKit server URL; `null` outside a call. Gates rendering alongside `room`. */
  livekitUrl: string | null;
}

/**
 * Always-mounted host for the agent's audio sink.
 *
 * The agent's voice is played by LiveKit's `<RoomAudioRenderer>`, which attaches
 * the remote (agent) audio tracks to the DOM. This used to live inside the
 * per-mode `<ConversationInputBar>`, so toggling the chat layout
 * (peek ↔ detailed ↔ focused) remounted it at a different position in the tree
 * and the agent's audio cut out mid-call — which presented to the user as the
 * call "disconnecting". The LiveKit room itself is owned imperatively by
 * `useVoiceConversation` and survives those toggles untouched; only the audio
 * renderer needed a stable mount.
 *
 * `ConversationInner` renders this once (it is mounted for the lifetime of the
 * v2 app), keeping a single audio sink alive for the whole duration of the room
 * regardless of layout mode. Renders nothing until a room exists.
 *
 * Note: we deliberately render only `RoomContext.Provider` + `RoomAudioRenderer`
 * here — NOT `<LiveKitRoom>`, whose `connect={false}` mount effect calls
 * `room.disconnect()` and would abort the hook's in-flight connect (the
 * "Client initiated disconnect" footgun). The room is connected imperatively, so
 * this is context-only.
 */
export function PersistentVoiceAudio({ room, livekitUrl }: PersistentVoiceAudioProps) {
  if (!room || !livekitUrl) return null;

  return (
    <RoomContext.Provider value={room}>
      <RoomAudioRenderer />
    </RoomContext.Provider>
  );
}
